import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, CheckCircle2, Inbox } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_STYLES = {
  deletion: "bg-warn-soft text-warn",
  takedown: "bg-gold-soft text-yellow-700",
  access: "bg-trust-soft text-trust",
  correction: "bg-trust-soft text-trust",
  other: "bg-gray-100 text-gray-500",
};

export const RequestsPanel = ({ token }) => {
  const [items, setItems] = useState(null);
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/data-requests`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(data.requests);
    } catch {
      setItems([]);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const resolve = async (id) => {
    setBusyId(id);
    try {
      await axios.post(`${API}/admin/data-requests/${id}/resolve`, {}, { headers: { Authorization: `Bearer ${token}` } });
      await load();
    } finally {
      setBusyId("");
    }
  };

  if (items === null) {
    return <div className="flex justify-center py-20"><Loader2 size={24} className="animate-spin text-trust" /></div>;
  }
  if (items.length === 0) {
    return (
      <div data-testid="requests-empty-state" className="rounded-2xl bg-white border border-black/5 p-12 text-center">
        <Inbox size={28} className="mx-auto text-gray-300" />
        <p className="mt-3 text-sm text-gray-500">No data or takedown requests right now.</p>
      </div>
    );
  }

  return (
    <div data-testid="requests-panel" className="space-y-4">
      {items.map((r, i) => (
        <article key={r.id} data-testid={`request-card-${i}`} className="rounded-2xl bg-white border border-black/5 shadow-sm p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className={`rounded-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider ${TYPE_STYLES[r.type] || TYPE_STYLES.other}`}>
                {r.type}
              </span>
              <span className="text-sm font-semibold">{r.email}</span>
            </div>
            <span className="text-xs text-gray-400">
              {new Date(r.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
              {r.status === "resolved" && <span className="ml-2 font-bold text-verified uppercase tracking-wider">Resolved</span>}
            </span>
          </div>
          <p className="mt-3 text-sm text-gray-600 leading-relaxed">{r.message}</p>
          {r.status !== "resolved" && (
            <button data-testid={`request-resolve-${i}`} onClick={() => resolve(r.id)} disabled={!!busyId}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-verified text-white px-5 py-2.5 text-xs font-semibold transition-opacity duration-200 hover:opacity-90 disabled:opacity-50">
              {busyId === r.id ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Mark resolved
            </button>
          )}
        </article>
      ))}
    </div>
  );
};
